import { InteractionStatus, InteractionType } from '@azure/msal-browser';
import {
  MsalAuthenticationTemplate,
  MsalProvider,
  useIsAuthenticated,
  useMsal,
} from '@azure/msal-react';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { SecureRoute, Security } from '@okta/okta-react';
import { loginRequestConfig, msalInstance } from 'config/auth';
import { oktaAuth } from 'config/oktaAuth';
import { useTeamsAuthContext } from 'containers/TeamsAuthContainer/TeamsAuthContext';
import React, { useEffect, useState } from 'react';
import { Route, RouteProps, useHistory } from 'react-router-dom';
import { AppService } from 'services';
import { getAccessToken } from 'utils/auth';
import { checkInTeams } from 'utils/function';
import { AuthProvider, useAuth, hasAuthParams } from "react-oidc-context";
import { oidcConfig, signOut } from 'config/oidcAuth';
import { AUTH } from 'constants/authConstants';
import { ChatPath, HomePath } from 'utils/routes';

interface IAuthenTemplate {
  children: any;
  checkAuthenticated: () => void;
  onAuthenticatedSuccess: (i: boolean) => void;
  oidcAuthen: () => {}
}

export function AuthenTemplate(props: IAuthenTemplate) {
  if (AppService.isAuthenticatedByOidc()) {
    const odicSignInConfig: any = {
      ...oidcConfig,
    };

    return (
      <AuthProvider {...odicSignInConfig} >
        <OidcAuthen {...props}>
          {props.children}
        </OidcAuthen>
      </AuthProvider>
    );
  }

  return AppService.isAuthenticatedByAzure() ? (
    <MsalProvider instance={msalInstance}>
      <AzureAuthen onAuthenticatedSuccess={props.onAuthenticatedSuccess}>
        {props.children}
      </AzureAuthen>
    </MsalProvider>
  ) : (
    <OktaAuthen {...props} />
  );
}

export function ProtectedRoute(props: RouteProps) {
  return AppService.isAuthenticatedByAzure() || AppService.isAuthenticatedByOidc() ? (
    <Route {...props} />
  ) : (
    <SecureRoute {...props} />
  );
}

const OidcAuthen = ({ children, onAuthenticatedSuccess, oidcAuthen }: any) => {
  const auth = useAuth();

  const history = useHistory();

  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const { isAuthenticated, activeNavigator, isLoading, user } = auth;

  const { id_token: idToken, expires_at: expiresAt, profile: userProfile } = user || {};
  useEffect(() => {
    if (isAuthenticated) {
      const currentUser = {
        idToken,
        expiresAt,
        email: userProfile?.email,
        username: userProfile?.preferred_username
      };
      localStorage.setItem(AUTH.CURRENT_USER, JSON.stringify(currentUser));
      onAuthenticatedSuccess(isAuthenticated);
      oidcAuthen(auth);
    }
  }, [isAuthenticated, idToken, userProfile?.email]);

  useEffect(() => {
    return auth.events.addAccessTokenExpiring(() => {
      console.log("You're about to be signed out due to inactivity.");
      auth.signinSilent().then((data: any ) => {
        const { id_token: idToken, expires_at: expiresAt, profile: userProfile } = data || {};
        if(data && idToken) {
          const updatedUser =  {
            idToken,
            expiresAt,
            email: userProfile?.email,
            username: userProfile?.preferred_username
          };
          localStorage.setItem("CURRENT_USER", JSON.stringify(updatedUser));
        }
      }).catch((error)=>{
        console.error("Cannot refreh token, need to signout", error);
        signOut();
      });
    });
  }, [auth.events]);
  
  useEffect(() => {
    if (!hasAuthParams() &&
      !isAuthenticated && !activeNavigator && !isLoading &&
      !hasTriedSignin
    ) {
      auth.signinRedirect();
      setHasTriedSignin(true);
    }
  }, [auth, hasTriedSignin]);
  
  useEffect(() => {
    const isAtHomePage =  [HomePath, ChatPath].includes(history.location.pathname);
    if (!isAtHomePage) return;

    const isOidcAuthenticated = AppService.isAuthenticatedByOidc() && isAuthenticated && history.location.search;
    if (!isOidcAuthenticated) return;

    const originalPathKey = 'original_path';
    const originalPath = sessionStorage.getItem(originalPathKey);
    const hasRedirectPath = !!originalPath && originalPath !== HomePath;

    if (hasRedirectPath) {
      sessionStorage.removeItem(originalPathKey);
      history.replace(originalPath);
      return;
    }

    history.replace(HomePath);
  }, [auth, history]);

  if (isAuthenticated) {
    return (children);
  }

  return <div>Signing you in...</div>;
};


const OktaAuthen = (props: any) => {
  const history = useHistory();

  const restoreOriginalUri = (_oktaAuth: any, originalUri: any) => {
    history.replace(toRelativeUrl(originalUri || '/', window.location.origin));
    props.checkAuthenticated();
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      {props.children}
    </Security>
  );
};

const AzureAuthen = ({ children, onAuthenticatedSuccess }: any) => {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const { setShouldLogin: setShouldLoginWithTeams } = useTeamsAuthContext();

  const isInTeams = checkInTeams();

  useEffect(() => {
    if (isAuthenticated && inProgress === InteractionStatus.None) {
      const checkToken = async () => {
        await getAccessToken();
        onAuthenticatedSuccess(isAuthenticated);
      };
      checkToken();
    }
  }, [instance, inProgress, isAuthenticated]);

  return (
    <MsalAuthenticationTemplate
      interactionType={isInTeams ? InteractionType.Silent : InteractionType.Redirect}
      authenticationRequest={loginRequestConfig}
      errorComponent={e => {
        isInTeams && setShouldLoginWithTeams(true);
        return <p>An Error Occurred: </p>;
      }}
      loadingComponent={() => <p>Authentication in progress. </p>}
    >
      {children}
    </MsalAuthenticationTemplate>
  );
};
